import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, Truck, Banknote, ChevronRight, Lock, Tag, MapPin, ChevronDown } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

// UAE emirate-based shipping rates (kept in sync with backend UAE_SHIPPING)
const SHIPPING_RATES = {
  'Dubai':          { cost: 15,  freeThreshold: 200, delivery: '1 working day' },
  'Sharjah':        { cost: 20,  freeThreshold: 200, delivery: '1–2 working days' },
  'Ajman':          { cost: 20,  freeThreshold: 200, delivery: '1–2 working days' },
  'Abu Dhabi':      { cost: 25,  freeThreshold: 300, delivery: '1–2 working days' },
  'Ras Al Khaimah': { cost: 35,  freeThreshold: 400, delivery: '2–3 working days' },
  'Fujairah':       { cost: 35,  freeThreshold: 400, delivery: '2–3 working days' },
  'Umm Al Quwain':  { cost: 35,  freeThreshold: 400, delivery: '2–3 working days' },
};
const DEFAULT_RATE = { cost: 25, freeThreshold: 200, delivery: '2–3 working days' };

function getShippingCost(emirate, subtotal, freeShippingCoupon) {
  if (freeShippingCoupon) return 0;
  const rate = SHIPPING_RATES[emirate] || DEFAULT_RATE;
  return subtotal >= rate.freeThreshold ? 0 : rate.cost;
}

const PAYMENT_METHODS = [
  { id: 'cod', label: 'Cash on Delivery', icon: Banknote, desc: 'Pay when you receive your order' },
  { id: 'card', label: 'Credit / Debit Card', icon: CreditCard, desc: 'Visa, Mastercard, AMEX (placeholder)' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: Truck, desc: 'Transfer to our UAE bank account' },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, clearCart } = useCart();
  const { user, token, getHeaders, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [address, setAddress] = useState({
    full_name: user?.name || '',
    phone: user?.phone || '',
    street: '',
    area: '',
    city: 'Dubai',
    emirate: 'Dubai',
    country: 'UAE',
  });

  // Coupon passed from CartPage
  const coupon = location.state?.coupon || null;
  const discount = coupon?.discount || 0;
  const freeShipping = coupon?.free_shipping || false;

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    if (cart.items.length === 0) { navigate('/cart'); return; }
    window.scrollTo(0, 0);
    // Load saved addresses
    axios.get(`${API}/auth/addresses`, { headers: getHeaders() })
      .then(r => setSavedAddresses(r.data || []))
      .catch(() => {});
  }, [user, authLoading, cart]);

  const setAddr = (k, v) => setAddress(a => ({ ...a, [k]: v }));

  const applyAddress = (saved) => {
    setAddress({
      full_name: saved.full_name,
      phone: saved.phone,
      street: saved.street,
      area: saved.area,
      city: saved.city,
      emirate: saved.emirate,
      country: saved.country || 'UAE',
    });
    setShowAddressPicker(false);
    toast.success(`Address "${saved.label}" applied`);
  };

  const validateShipping = () => {
    if (!address.full_name || !address.phone || !address.street || !address.area || !address.emirate) {
      toast.error('Please fill all required shipping fields');
      return false;
    }
    return true;
  };

  const placeOrder = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        shipping_address: address,
        payment_method: paymentMethod,
        coupon_code: coupon?.code || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order placed successfully!');
      navigate(`/order-success/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const currentRate = SHIPPING_RATES[address.emirate] || DEFAULT_RATE;
  const shippingCost = getShippingCost(address.emirate, cart.total, freeShipping);
  const orderTotal = Math.max(0, cart.total - discount + shippingCost);

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <div className="pt-[105px]">
        <div className="bg-surface border-b border-white/5 py-5">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <h1 className="font-heading font-black text-2xl md:text-3xl uppercase text-white">Checkout</h1>
            <div className="flex items-center gap-2 mt-2">
              {['Shipping', 'Payment', 'Review'].map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${step > i + 1 ? 'text-primary' : step === i + 1 ? 'text-white' : 'text-white/30'}`}>
                    <span className={`w-5 h-5 flex items-center justify-center text-xs ${step > i + 1 ? 'bg-primary text-black' : step === i + 1 ? 'bg-white text-black' : 'bg-white/10 text-white/30'}`}>{i + 1}</span>
                    {s}
                  </div>
                  {i < 2 && <ChevronRight size={12} className="text-white/20" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main form */}
            <div className="lg:col-span-2">

              {/* Step 1: Shipping */}
              {step === 1 && (
                <div className="bg-surface border border-white/5 p-6" data-testid="shipping-form">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-heading font-bold text-lg uppercase text-white flex items-center gap-2">
                      <Truck size={18} className="text-primary" /> Shipping Details
                    </h2>
                    {savedAddresses.length > 0 && (
                      <div className="relative">
                        <button
                          onClick={() => setShowAddressPicker(v => !v)}
                          className="flex items-center gap-1.5 text-xs text-primary border border-primary/30 hover:border-primary px-3 py-2 transition-colors"
                          data-testid="saved-address-picker-btn"
                        >
                          <MapPin size={12} /> Saved Addresses <ChevronDown size={11} />
                        </button>
                        {showAddressPicker && (
                          <div className="absolute right-0 top-full mt-1 w-72 bg-surface border border-white/10 z-20 shadow-xl" data-testid="saved-address-dropdown">
                            {savedAddresses.map(a => (
                              <button
                                key={a.id}
                                onClick={() => applyAddress(a)}
                                className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-0 transition-colors"
                                data-testid={`saved-addr-${a.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-primary uppercase tracking-wider">{a.label}</span>
                                  {a.is_default && <span className="text-xs text-white/40 border border-white/10 px-1">default</span>}
                                </div>
                                <p className="text-xs text-white/70 mt-0.5 truncate">{a.full_name} — {a.street}, {a.emirate}</p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Full Name *</label>
                      <input value={address.full_name} onChange={e => setAddr('full_name', e.target.value)}
                        placeholder="Ahmed Al Rashidi" className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                        style={{ borderRadius: 0 }} data-testid="shipping-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Phone Number *</label>
                      <input value={address.phone} onChange={e => setAddr('phone', e.target.value)}
                        placeholder="+971 50 XXX XXXX" className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                        style={{ borderRadius: 0 }} data-testid="shipping-phone" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Street Address / Building *</label>
                      <input value={address.street} onChange={e => setAddr('street', e.target.value)}
                        placeholder="Villa 12, Al Safa Street" className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                        style={{ borderRadius: 0 }} data-testid="shipping-street" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Area / District *</label>
                      <input value={address.area} onChange={e => setAddr('area', e.target.value)}
                        placeholder="Jumeirah, Al Quoz..." className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                        style={{ borderRadius: 0 }} data-testid="shipping-area" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Emirate *</label>
                      <select value={address.emirate} onChange={e => setAddr('emirate', e.target.value)}
                        className="w-full bg-background border border-white/10 focus:border-primary text-white px-4 py-3 outline-none text-sm transition-colors"
                        style={{ borderRadius: 0 }} data-testid="shipping-emirate">
                        {EMIRATES.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">City</label>
                      <input value={address.city} onChange={e => setAddr('city', e.target.value)}
                        className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                        style={{ borderRadius: 0 }} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Country</label>
                      <input value="United Arab Emirates" readOnly
                        className="w-full bg-background border border-white/5 text-white/40 px-4 py-3 outline-none text-sm"
                        style={{ borderRadius: 0 }} />
                    </div>
                  </div>

                  {/* Live shipping preview */}
                  <div className="mt-5 p-4 bg-background border border-white/5" data-testid="shipping-rate-preview">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck size={13} className="text-primary" />
                        <span className="text-xs font-bold uppercase tracking-wider text-white/70">Delivery to {address.emirate}</span>
                      </div>
                      <div className="text-right">
                        {freeShipping || cart.total >= currentRate.freeThreshold ? (
                          <span className="text-xs font-bold text-green-400">FREE</span>
                        ) : (
                          <span className="text-xs font-mono text-primary font-bold">AED {currentRate.cost}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-white/40">Estimated delivery: <span className="text-white/60">{currentRate.delivery}</span></span>
                      {!freeShipping && cart.total < currentRate.freeThreshold && (
                        <span className="text-xs text-white/40">
                          Free over <span className="text-primary font-mono">AED {currentRate.freeThreshold}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button onClick={() => { if (validateShipping()) setStep(2); }}
                      className="flex items-center gap-2 bg-primary text-black font-bold uppercase tracking-wider px-8 py-3 hover:bg-white transition-colors text-sm"
                      data-testid="shipping-next-btn">
                      Continue to Payment <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Payment */}
              {step === 2 && (
                <div className="bg-surface border border-white/5 p-6" data-testid="payment-form">
                  <h2 className="font-heading font-bold text-lg uppercase text-white mb-5 flex items-center gap-2">
                    <CreditCard size={18} className="text-primary" /> Payment Method
                  </h2>
                  <div className="space-y-3">
                    {PAYMENT_METHODS.map(({ id, label, icon: Icon, desc }) => (
                      <button key={id} onClick={() => setPaymentMethod(id)}
                        className={`w-full flex items-center gap-4 p-4 border transition-all text-left ${paymentMethod === id ? 'border-primary bg-primary/5' : 'border-white/10 hover:border-white/20'}`}
                        data-testid={`payment-method-${id}`}>
                        <div className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === id ? 'border-primary' : 'border-white/30'}`}>
                          {paymentMethod === id && <div className="w-2 h-2 bg-primary" />}
                        </div>
                        <Icon size={20} className={paymentMethod === id ? 'text-primary' : 'text-white/60'} />
                        <div>
                          <p className={`text-sm font-bold ${paymentMethod === id ? 'text-white' : 'text-white/70'}`}>{label}</p>
                          <p className="text-xs text-muted-foreground">{desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  {paymentMethod === 'card' && (
                    <div className="mt-4 p-4 bg-background border border-white/5">
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <Lock size={12} className="text-primary" />
                        Card payment integration coming soon. Please use COD or Bank Transfer.
                      </p>
                    </div>
                  )}
                  <div className="mt-6 flex justify-between">
                    <button onClick={() => setStep(1)} className="text-sm text-muted-foreground hover:text-primary transition-colors">← Back to Shipping</button>
                    <button onClick={() => setStep(3)}
                      className="flex items-center gap-2 bg-primary text-black font-bold uppercase tracking-wider px-8 py-3 hover:bg-white transition-colors text-sm"
                      data-testid="payment-next-btn">
                      Review Order <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Review */}
              {step === 3 && (
                <div className="bg-surface border border-white/5 p-6" data-testid="order-review">
                  <h2 className="font-heading font-bold text-lg uppercase text-white mb-5">Review Your Order</h2>

                  <div className="border border-white/5 p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Shipping To</p>
                      <button onClick={() => setStep(1)} className="text-xs text-primary hover:text-white transition-colors">Edit</button>
                    </div>
                    <p className="text-sm text-white font-semibold">{address.full_name}</p>
                    <p className="text-sm text-white/60">{address.street}, {address.area}</p>
                    <p className="text-sm text-white/60">{address.emirate}, {address.country}</p>
                    <p className="text-sm text-white/60">{address.phone}</p>
                    <div className="mt-2 flex items-center gap-1.5">
                      <Truck size={11} className="text-primary" />
                      <span className="text-xs text-white/50">Est. delivery: <span className="text-white/70">{currentRate.delivery}</span></span>
                    </div>
                  </div>

                  <div className="border border-white/5 p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Payment Method</p>
                      <button onClick={() => setStep(2)} className="text-xs text-primary hover:text-white transition-colors">Edit</button>
                    </div>
                    <p className="text-sm text-white font-semibold">{PAYMENT_METHODS.find(p => p.id === paymentMethod)?.label}</p>
                  </div>

                  <div className="border border-white/5 p-4 mb-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Order Items ({cart.items.length})</p>
                    <div className="space-y-2">
                      {cart.items.map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <img src={item.product?.images?.[0]} alt="" className="w-12 h-12 object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white truncate">{item.product?.name}</p>
                            <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                          <span className="text-sm font-mono text-primary font-bold">AED {item.subtotal?.toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button onClick={placeOrder} disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-black font-bold uppercase tracking-wider py-4 hover:bg-white transition-colors text-base disabled:opacity-60"
                    data-testid="place-order-btn">
                    <Lock size={16} />
                    {loading ? 'Placing Order...' : `Place Order — AED ${orderTotal.toFixed(0)}`}
                  </button>
                  <p className="text-xs text-muted-foreground text-center mt-3 flex items-center justify-center gap-1">
                    <Lock size={10} className="text-primary" /> Secured by SSL encryption
                  </p>
                </div>
              )}
            </div>

            {/* Order summary sidebar */}
            <div>
              <div className="bg-surface border border-white/5 p-5 sticky top-24">
                <h2 className="font-heading font-bold text-base uppercase text-white mb-4">Order Summary</h2>
                <div className="space-y-2 mb-4">
                  {cart.items.map((item, i) => (
                    <div key={i} className="flex justify-between items-center text-xs gap-2">
                      <span className="text-white/70 truncate flex-1">{item.product?.name} × {item.quantity}</span>
                      <span className="text-white font-mono flex-shrink-0">AED {item.subtotal?.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-white font-mono">AED {cart.total?.toFixed(0)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm" data-testid="checkout-discount">
                      <span className="text-green-400 flex items-center gap-1"><Tag size={11} /> {coupon.code}</span>
                      <span className="text-green-400 font-mono font-bold">− AED {discount.toFixed(0)}</span>
                    </div>
                  )}
                  {freeShipping && !discount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-400 flex items-center gap-1"><Tag size={11} /> {coupon.code}</span>
                      <span className="text-green-400 font-semibold">FREE SHIP</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Truck size={11} /> Shipping
                    </span>
                    <span className={shippingCost === 0 ? 'text-green-400 font-semibold' : 'text-white font-mono'}>
                      {shippingCost === 0 ? 'FREE' : `AED ${shippingCost}`}
                    </span>
                  </div>
                  <div className="text-xs text-white/40 flex items-center gap-1 pb-1">
                    <Truck size={10} /> {currentRate.delivery} to {address.emirate}
                  </div>
                </div>
                <div className="border-t border-white/20 pt-3 mt-1 flex justify-between items-center">
                  <span className="font-heading font-bold text-base text-white">TOTAL</span>
                  <span className="price-tag text-primary font-black text-lg">AED {orderTotal.toFixed(0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
